/**
 * WooCommerce Store API client for the header cart.
 *
 * Replaces the woocommerce/mini-cart block. Reads are plain GETs; writes need
 * the rotating Store API nonce, which the API hands back on every response via
 * the `Nonce` header, so we keep the freshest one we've seen.
 */

let nonce = null;

function url(base, path) {
    return base.replace(/\/$/, '') + '/' + path;
}

function capture(response) {
    const fresh = response.headers.get('Nonce');
    if (fresh) {
        nonce = fresh;
    }
    return response;
}

function headers() {
    const value = { 'Content-Type': 'application/json' };
    if (nonce) {
        value.Nonce = nonce;
    }
    return value;
}

/**
 * @param {string} base TSA_CHROME.storeApi
 * @return {Promise<Object>} cart payload
 */
export function fetchCart(base) {
    return fetch(url(base, 'cart'), { credentials: 'include' })
        .then(capture)
        .then((response) => {
            if (!response.ok) {
                throw new Error('cart request failed');
            }
            return response.json();
        });
}

/**
 * @param {string} base
 * @param {string} key  cart item key
 * @return {Promise<Object>} updated cart
 */
export function removeCartItem(base, key) {
    return fetch(url(base, 'cart/remove-item'), {
        method: 'POST',
        credentials: 'include',
        headers: headers(),
        body: JSON.stringify({ key }),
    })
        .then(capture)
        .then((response) => {
            if (!response.ok) {
                throw new Error('remove item failed');
            }
            return response.json();
        });
}

/**
 * @param {string} base
 * @param {string} key
 * @param {number} quantity
 * @return {Promise<Object>} updated cart
 */
export function updateCartItem(base, key, quantity) {
    return fetch(url(base, 'cart/update-item'), {
        method: 'POST',
        credentials: 'include',
        headers: headers(),
        body: JSON.stringify({ key, quantity }),
    })
        .then(capture)
        .then((response) => {
            if (!response.ok) {
                throw new Error('update item failed');
            }
            return response.json();
        });
}

/**
 * Store API money values are minor-unit strings ("129500" = $1,295.00).
 *
 * @param {string|number} value
 * @param {Object}        totals cart.totals (carries the currency settings)
 * @return {string}
 */
export function formatPrice(value, totals) {
    if (!totals) {
        return '';
    }

    const minor = Number(totals.currency_minor_unit || 2);
    const amount = Number(value || 0) / Math.pow(10, minor);
    const parts = Math.abs(amount).toFixed(minor).split('.');

    // Group the integer part only — running the grouping regex over the whole
    // string would punctuate the cents too.
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, totals.currency_thousand_separator || ',');

    const formatted = parts.join(totals.currency_decimal_separator || '.');

    return (amount < 0 ? '-' : '') + (totals.currency_prefix || '') + formatted + (totals.currency_suffix || '');
}
